import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { I_Building, I_Camera, I_Check, I_ChevDown, I_Doc, I_Msg, I_Plus, I_Spark, I_Trash, I_Users, I_X } from "./icons";
import { Ava, Pill, Spinner, YesNo } from "./ui";
import { STATUSES_P, TYPES, sStyle } from "../constants.js";
import { logActivity } from "../lib/activity.js";
import { bucketIncludes, fmtDate, fmtINR, normBucket, waHref } from "../lib/format.js";
import { signedUrl } from "../lib/storage.js";
import { btnD, btnG, glass, glassDeep, glassHov, heading, input, label, monoFont } from "../styles.js";
import { sb } from "../supabase.js";

const PropertyDetail = ({
    propertyId,
    session,
    clients,
    visits,
    onClose,
    onChanged,
    onOpenClient,
    onAddVisit,
  }) => {
    const [p, setP] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [pdfs, setPdfs] = useState([]);
    const [photoUrls, setPhotoUrls] = useState({});
    const [saving, setSaving] = useState(false);
    const [viewer, setViewer] = useState(null);
    const [confirmDel, setConfirmDel] = useState(false);
    const [showMatch, setShowMatch] = useState(true);
    const myVisits = useMemo(
      () =>
        visits
          .filter((v) => v.property_id === propertyId)
          .sort((a, b) => new Date(b.visited_at) - new Date(a.visited_at)),
      [visits, propertyId],
    );
    const clientsById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    useEffect(() => {
      let on = true;
      (async () => {
        const { data: pd } = await sb.from("properties").select("*").eq("id", propertyId).single();
        if (on) setP(pd);
        const { data: ph } = await sb
          .from("property_photos")
          .select("*")
          .eq("property_id", propertyId)
          .order("position", {
            ascending: true,
          });
        if (on) setPhotos(ph || []);
        const { data: pf } = await sb
          .from("property_pdfs")
          .select("*")
          .eq("property_id", propertyId)
          .order("created_at", {
            ascending: true,
          });
        if (on) setPdfs(pf || []);
      })();
      return () => {
        on = false;
      };
    }, [propertyId]);
    useEffect(() => {
      (async () => {
        const next = {};
        for (const ph of photos) {
          try {
            next[ph.id] = await signedUrl(ph.file_path);
          } catch (e) {}
        }
        setPhotoUrls(next);
      })();
    }, [photos]);
    const saveTimer = useRef(null);
    const patch = (delta, immediate = false) => {
      const next = {
        ...p,
        ...delta,
      };
      setP(next);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      const doSave = async () => {
        setSaving(true);
        const { id, created_at, updated_at, ...fields } = next;
        await sb.from("properties").update(fields).eq("id", propertyId);
        setSaving(false);
        onChanged && onChanged();
      };
      if (immediate) doSave();
      else saveTimer.current = setTimeout(doSave, 700);
    };

    // smart matching
    const matches = useMemo(() => {
      if (!p) return [];
      const typesMatch = (cw, pi) => {
        if (!cw || !pi) return false;
        if (cw === pi) return true;
        if (cw === "Land+House" && (pi === "Land" || pi === "House")) return true;
        if (pi === "Land+House" && (cw === "Land" || cw === "House")) return true;
        return false;
      };
      return clients
        .filter((c) => c.status !== "Dropped" && !c.allotted)
        .map((c) => {
          let score = 0,
            reasons = [];
          if (c.budget_bucket && bucketIncludes(c.budget_bucket, p.price_lakhs)) {
            score += 50;
            reasons.push("budget fits");
          } else if (!c.budget_bucket || normBucket(c.budget_bucket) === "UNSPECIFIED") {
            score += 10;
            reasons.push("budget open");
          }
          if (typesMatch(c.requirement_type, p.type)) {
            score += 30;
            reasons.push("requirement matches");
          }
          if (c.locality_pref && p.locality) {
            const cl = c.locality_pref.toLowerCase().split(/[,/]/)[0].trim();
            const pl = p.locality.toLowerCase();
            if (cl && (pl.includes(cl) || cl.includes(pl))) {
              score += 20;
              reasons.push("locality fits");
            }
          }
          if (c.priority === "Urgent") {
            score += 8;
            reasons.push("urgent priority");
          } else if (c.priority === "High") {
            score += 4;
          }
          if (c.sanctioned) {
            score += 3;
          }
          return {
            c,
            score,
            reasons,
          };
        })
        .filter((m) => m.score >= 30)
        .sort((a, b) => b.score - a.score)
        .slice(0, 20);
    }, [p, clients]);
    const uploadCover = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      const img = new Image(),
        fr = new FileReader();
      fr.onload = () => {
        img.onload = () => {
          const W = 800,
            ratio = img.width / img.height,
            H = Math.round(W / ratio);
          const cv = document.createElement("canvas");
          cv.width = W;
          cv.height = H;
          cv.getContext("2d").drawImage(img, 0, 0, W, H);
          patch(
            {
              cover_photo: cv.toDataURL("image/jpeg", 0.78),
            },
            true,
          );
        };
        img.src = fr.result;
      };
      fr.readAsDataURL(f);
    };
    const uploadPhotos = async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      for (const f of files) {
        try {
          const safe = (f.name || "photo").replace(/[^\w.-]+/g, "_");
          const path = `properties/${propertyId}/${crypto.randomUUID()}-${safe}`;
          await sb.storage.from("estate-files").upload(path, f);
          const { data } = await sb
            .from("property_photos")
            .insert({
              property_id: propertyId,
              file_path: path,
              file_name: f.name,
              mime_type: f.type,
              created_by: session.user.id,
            })
            .select()
            .single();
          setPhotos((ps) => [...ps, data]);
        } catch (err) {
          alert("Couldn't upload: " + (err.message || ""));
        }
      }
      e.target.value = "";
    };
    const uploadPdf = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      try {
        const safe = (f.name || "doc.pdf").replace(/[^\w.-]+/g, "_");
        const path = `properties/${propertyId}/${crypto.randomUUID()}-${safe}`;
        await sb.storage.from("estate-files").upload(path, f);
        const { data } = await sb
          .from("property_pdfs")
          .insert({
            property_id: propertyId,
            file_path: path,
            file_name: f.name,
            size_bytes: f.size,
            created_by: session.user.id,
          })
          .select()
          .single();
        setPdfs((ps) => [...ps, data]);
      } catch (err) {
        alert("Couldn't upload PDF: " + (err.message || ""));
      }
      e.target.value = "";
    };
    const delPhoto = async (ph) => {
      try {
        await sb.storage.from("estate-files").remove([ph.file_path]);
        await sb.from("property_photos").delete().eq("id", ph.id);
        setPhotos((x) => x.filter((y) => y.id !== ph.id));
      } catch (e) {}
    };
    const delPdf = async (pd) => {
      try {
        await sb.storage.from("estate-files").remove([pd.file_path]);
        await sb.from("property_pdfs").delete().eq("id", pd.id);
        setPdfs((x) => x.filter((y) => y.id !== pd.id));
      } catch (e) {}
    };
    const setCover = async (ph) => {
      const url = photoUrls[ph.id];
      if (!url) return;
      try {
        const r = await fetch(url);
        const blob = await r.blob();
        const img = new Image();
        const fr = new FileReader();
        fr.onload = () => {
          img.onload = () => {
            const W = 800,
              H = Math.round((W * img.height) / img.width);
            const cv = document.createElement("canvas");
            cv.width = W;
            cv.height = H;
            cv.getContext("2d").drawImage(img, 0, 0, W, H);
            patch(
              {
                cover_photo: cv.toDataURL("image/jpeg", 0.78),
              },
              true,
            );
          };
          img.src = fr.result;
        };
        fr.readAsDataURL(blob);
      } catch (e) {}
    };
    const openPdf = async (pd) => {
      try {
        const u = await signedUrl(pd.file_path);
        setViewer({
          kind: "pdf",
          url: u,
          name: pd.file_name,
        });
      } catch (e) {
        alert("Couldn't open.");
      }
    };
    const del = async () => {
      const paths = [...photos.map((x) => x.file_path), ...pdfs.map((x) => x.file_path)];
      if (paths.length) await sb.storage.from("estate-files").remove(paths);
      await sb.from("properties").delete().eq("id", propertyId);
      logActivity(
        session.user.id,
        "updated",
        "property",
        propertyId,
        `Deleted property ${p.title}`,
      );
      onChanged && onChanged();
      onClose();
    };
    if (!p)
      return (
        <React.Fragment>
          <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40" onClick={onClose} />
          <div
            className={`fixed inset-y-0 right-0 w-full md:w-[760px] z-50 ${glassDeep} flex items-center justify-center text-slate-400`}
          >
            <Spinner size={20} />
          </div>
        </React.Fragment>
      );
    const waText = `Hi, regarding property ${p.code || ""} — ${p.title} (${fmtINR(p.price_lakhs)})`;
    return (
      <React.Fragment>
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 animate-[fadein_.15s]"
          onClick={onClose}
        />
        <div
          className={`fixed inset-y-0 right-0 w-full md:w-[760px] z-50 ${glassDeep} overflow-y-auto animate-[slidein_.22s]`}
        >
          <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur-xl border-b border-white/[0.06] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[11px] text-slate-400">
              <span className="text-teal-300/80" style={monoFont}>
                {p.code || "Property"}
              </span>
              {saving ? (
                <span className="flex items-center gap-1">
                  <Spinner size={10} />
                  saving
                </span>
              ) : (
                <span className="flex items-center gap-1 text-emerald-300/80">
                  <I_Check size={11} />
                  saved
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400"
            >
              <I_X size={16} />
            </button>
          </div>
          <div className="relative aspect-[16/9] bg-white/[0.04]">
            {p.cover_photo ? (
              <img src={p.cover_photo} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-600">
                <I_Building size={48} />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent" />
            <label className="absolute top-3 right-3 cursor-pointer text-[11px] px-2 py-1 rounded bg-slate-950/60 backdrop-blur text-slate-200 hover:bg-slate-950/80 border border-white/[0.1] flex items-center gap-1">
              <I_Camera size={11} />
              Cover
              <input type="file" accept="image/*" className="hidden" onChange={uploadCover} />
            </label>
          </div>
          <div className="p-5 space-y-5 -mt-4 relative z-10">
            <div>
              <input
                value={p.title || ""}
                onChange={(e) =>
                  patch({
                    title: e.target.value,
                  })
                }
                placeholder="Property title"
                className="w-full text-3xl text-slate-100 bg-transparent border-b border-white/[0.06] focus:border-teal-300/50 focus:outline-none pb-1"
                style={heading}
              />
              <div className="flex items-center gap-2 mt-3">
                {p.status && <Pill label={p.status} s={sStyle[p.status] || sStyle.Available} />}
                <button onClick={() => onAddVisit(propertyId)} className={`${btnG} text-xs`}>
                  <I_Plus size={12} />
                  Log visit
                </button>
                <a
                  href={waHref("", waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 border border-emerald-500/20"
                >
                  <I_Msg size={12} />
                  Share via WhatsApp
                </a>
              </div>
            </div>
            {matches.length > 0 && (
              <div className={`${glass} rounded-xl p-4 border-amber-300/10`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-amber-200">
                    <I_Spark size={15} />
                    <h3 className="text-base" style={heading}>
                      Matches your clients
                    </h3>
                    <span className="text-[10px] text-slate-400">· {matches.length} found</span>
                  </div>
                  <button
                    onClick={() => setShowMatch((v) => !v)}
                    className="text-[11px] text-slate-400 hover:text-slate-200 flex items-center gap-0.5"
                  >
                    {showMatch ? "Hide" : "Show"}
                    <I_ChevDown
                      size={11}
                      className={showMatch ? "rotate-180 transition" : "transition"}
                    />
                  </button>
                </div>
                {showMatch && (
                  <div className="space-y-2">
                    {matches.map(({ c, score, reasons }) => (
                      <button
                        key={c.id}
                        onClick={() => onOpenClient(c.id)}
                        className="w-full text-left p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.04] transition flex items-center gap-3"
                      >
                        <Ava name={c.name} photo={c.photo} size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-100 truncate">{c.name}</div>
                          <div className="text-[11px] text-slate-400">{reasons.join(" · ")}</div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="relative w-20 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                            <div
                              className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-300 to-amber-200 rounded-full shadow-[0_0_8px_rgba(252,211,77,0.6)]"
                              style={{
                                width: `${Math.min(100, score)}%`,
                              }}
                            />
                          </div>
                          <div className="text-[10px] text-amber-200 mt-1" style={monoFont}>
                            {score}% fit
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Status</label>
                <select
                  value={p.status}
                  onChange={(e) =>
                    patch(
                      {
                        status: e.target.value,
                      },
                      true,
                    )
                  }
                  className={input}
                >
                  {STATUSES_P.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Type</label>
                <select
                  value={p.type || ""}
                  onChange={(e) =>
                    patch(
                      {
                        type: e.target.value,
                      },
                      true,
                    )
                  }
                  className={input}
                >
                  <option value="">—</option>
                  {TYPES.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Price (₹ lakhs)</label>
                <input
                  type="number"
                  value={p.price_lakhs ?? ""}
                  onChange={(e) =>
                    patch({
                      price_lakhs: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Negotiable</label>
                <select
                  value={p.negotiable ? "yes" : "no"}
                  onChange={(e) =>
                    patch(
                      {
                        negotiable: e.target.value === "yes",
                      },
                      true,
                    )
                  }
                  className={input}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className={label}>Allotted?</label>
                <YesNo
                  value={!!p.allotted}
                  onChange={(v) =>
                    patch(
                      {
                        allotted: v,
                      },
                      true,
                    )
                  }
                />
              </div>
              <div>
                <label className={label}>Land (cents)</label>
                <input
                  type="number"
                  value={p.land_cents ?? ""}
                  onChange={(e) =>
                    patch({
                      land_cents: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Built-up (sqft)</label>
                <input
                  type="number"
                  value={p.builtup_sqft ?? ""}
                  onChange={(e) =>
                    patch({
                      builtup_sqft: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>BHK</label>
                <input
                  value={p.bhk || ""}
                  onChange={(e) =>
                    patch({
                      bhk: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Facing</label>
                <input
                  value={p.facing || ""}
                  onChange={(e) =>
                    patch({
                      facing: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Locality</label>
                <input
                  value={p.locality || ""}
                  onChange={(e) =>
                    patch({
                      locality: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Panchayat</label>
                <input
                  value={p.panchayat || ""}
                  onChange={(e) =>
                    patch({
                      panchayat: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Taluk</label>
                <input
                  value={p.taluk || ""}
                  onChange={(e) =>
                    patch({
                      taluk: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>District</label>
                <input
                  value={p.district || ""}
                  onChange={(e) =>
                    patch({
                      district: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Survey no</label>
                <input
                  value={p.survey_no || ""}
                  onChange={(e) =>
                    patch({
                      survey_no: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Block no</label>
                <input
                  value={p.block_no || ""}
                  onChange={(e) =>
                    patch({
                      block_no: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
            </div>
            <div>
              <label className={label}>Description</label>
              <textarea
                value={p.description || ""}
                onChange={(e) =>
                  patch({
                    description: e.target.value,
                  })
                }
                rows={3}
                className={`${input} resize-y`}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={label + " mb-0"}>
                  Photos <span className="text-slate-500">· {photos.length}</span>
                </label>
                <label className={`${btnG} text-[11px] py-1.5 px-2.5 cursor-pointer`}>
                  <I_Plus size={11} />
                  Add photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple={true}
                    className="hidden"
                    onChange={uploadPhotos}
                  />
                </label>
              </div>
              {photos.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-3 border border-dashed border-white/[0.08] rounded-lg">
                  No photos yet.
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photos.map((ph) => (
                    <div
                      key={ph.id}
                      className="relative group aspect-square rounded-lg overflow-hidden bg-white/[0.04] border border-white/[0.06]"
                    >
                      {photoUrls[ph.id] ? (
                        <button
                          onClick={() =>
                            setViewer({
                              kind: "image",
                              url: photoUrls[ph.id],
                            })
                          }
                          className="w-full h-full"
                        >
                          <img src={photoUrls[ph.id]} className="w-full h-full object-cover" />
                        </button>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Spinner size={14} />
                        </div>
                      )}
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-0.5 transition">
                        <button
                          onClick={() => setCover(ph)}
                          title="Set as cover"
                          className="p-1 rounded bg-slate-950/70 text-amber-200 hover:bg-slate-950/90"
                        >
                          <I_Spark size={10} />
                        </button>
                        <button
                          onClick={() => delPhoto(ph)}
                          className="p-1 rounded bg-slate-950/70 text-rose-300 hover:bg-slate-950/90"
                        >
                          <I_X size={10} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={label + " mb-0"}>
                  Documents <span className="text-slate-500">· {pdfs.length}</span>
                </label>
                <label className={`${btnG} text-[11px] py-1.5 px-2.5 cursor-pointer`}>
                  <I_Plus size={11} />
                  Add PDF
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={uploadPdf}
                  />
                </label>
              </div>
              {pdfs.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-3 border border-dashed border-white/[0.08] rounded-lg">
                  No documents yet.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {pdfs.map((pd) => (
                    <div
                      key={pd.id}
                      className={`${glass} rounded-lg p-2.5 flex items-center gap-2`}
                    >
                      <I_Doc size={16} className="text-rose-300 shrink-0" />
                      <button
                        onClick={() => openPdf(pd)}
                        className="text-sm text-slate-200 truncate flex-1 text-left hover:text-slate-100"
                      >
                        {pd.file_name}
                      </button>
                      <button
                        onClick={() => delPdf(pd)}
                        className="text-slate-500 hover:text-rose-300"
                      >
                        <I_X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3 className="text-lg text-slate-100 mb-3 flex items-center gap-2" style={heading}>
                <I_Users size={16} className="text-amber-300" />
                Visitors{" "}
                <span className="text-slate-500 text-sm font-sans">· {myVisits.length}</span>
              </h3>
              {myVisits.length === 0 ? (
                <div className={`${glass} rounded-lg p-4 text-center text-sm text-slate-500`}>
                  No visits logged yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {myVisits.map((v) => {
                    const c = clientsById[v.client_id];
                    return (
                      <button
                        key={v.id}
                        onClick={() => (c ? onOpenClient(c.id) : null)}
                        className={`${glass} ${c ? glassHov : ""} w-full text-left p-3 rounded-lg flex items-center gap-3`}
                      >
                        <Ava name={c?.name || v.walkin_name || "?"} photo={c?.photo} size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-100 truncate">
                            {c?.name || v.walkin_name || "Walk-in visitor"}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {fmtDate(v.visited_at)}
                          </div>
                          {v.note && (
                            <div className="text-[11px] text-slate-500 truncate mt-0.5 italic">
                              "{v.note}"
                            </div>
                          )}
                        </div>
                        <div className="shrink-0">
                          {v.interested === true && (
                            <span className="text-[10px] uppercase tracking-wider text-emerald-300">
                              Interested
                            </span>
                          )}
                          {v.interested === false && (
                            <span className="text-[10px] uppercase tracking-wider text-stone-400">
                              Not interested
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="pt-2">
              {!confirmDel ? (
                <button onClick={() => setConfirmDel(true)} className={btnD}>
                  <I_Trash size={12} />
                  Delete property
                </button>
              ) : (
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-rose-300">
                    Delete this property, its photos and documents?
                  </span>
                  <button
                    onClick={() => setConfirmDel(false)}
                    className="px-2 py-0.5 rounded hover:bg-white/[0.06] text-slate-300"
                  >
                    Cancel
                  </button>
                  <button onClick={del} className="px-2 py-0.5 rounded bg-rose-600 text-white">
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
          {viewer && viewer.kind === "image" && (
            <div
              className="fixed inset-0 z-[60] bg-slate-950/95 flex items-center justify-center p-4"
              onClick={() => setViewer(null)}
            >
              <img src={viewer.url} className="max-w-full max-h-full rounded-lg" />
              <button className="absolute top-4 right-4 text-slate-300 hover:text-white">
                <I_X size={24} />
              </button>
            </div>
          )}
          {viewer && viewer.kind === "pdf" && (
            <div className="fixed inset-0 z-[60] bg-slate-950/90 flex flex-col">
              <div className="flex items-center justify-between p-3 text-slate-100 bg-slate-950">
                <span className="text-sm truncate flex items-center gap-2">
                  <I_Doc size={14} />
                  {viewer.name}
                </span>
                <div className="flex gap-2">
                  <a
                    href={viewer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${btnG} text-xs`}
                  >
                    New tab
                  </a>
                  <button onClick={() => setViewer(null)} className={`${btnG} text-xs`}>
                    Close
                  </button>
                </div>
              </div>
              <iframe src={viewer.url} title="pdf" className="flex-1 bg-white" />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

export { PropertyDetail };
